import { useEffect } from 'react';
import { captureAppException } from '../utils/errorCaptureUtils';
import { captureCustomEvent } from '../utils/posthogUtils';

/**
 * Hook to handle unhandled promise rejections globally
 * This helps catch PostHog internal errors and other unhandled rejections
 * that might not be properly caught by our error boundaries
 */
export const useUnhandledRejectionHandler = () => {
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      
      // Check if it's a PostHog internal error
      if (reason && typeof reason === 'string' && 
          reason.includes('Object Not Found Matching Id')) {
        
        // Log the PostHog error but don't let it crash the app
        console.warn('PostHog internal error:', reason);
        
        // Prevent the error from being reported to Sentry as unhandled
        event.preventDefault();
        
        // Capture as a custom event for monitoring
        captureCustomEvent('posthog_internal_error', {
          eventProperties: {
            error_message: reason,
            url: window.location.href,
            timestamp: new Date().toISOString()
          }
        });
        
        return;
      }
      
      // Handle other unhandled rejections
      if (reason instanceof Error) {
        // It's already an Error object, capture it directly
        captureAppException(reason, {
          tags: {
            context: 'unhandled_rejection',
            error_type: 'promise_rejection'
          }
        });
      } else if (typeof reason === 'string') {
        // Convert string to Error object
        const error = new Error(`Unhandled promise rejection: ${reason}`);
        captureAppException(error, {
          tags: {
            context: 'unhandled_rejection',
            error_type: 'promise_rejection_string',
            original_reason: reason
          }
        });
      } else {
        // Handle other types of rejections
        const error = new Error(`Unhandled promise rejection: ${JSON.stringify(reason)}`);
        captureAppException(error, {
          tags: {
            context: 'unhandled_rejection',
            error_type: 'promise_rejection_other',
            original_reason: reason
          }
        });
      }
      
      // Prevent the error from being reported to Sentry as unhandled
      event.preventDefault();
    };

    // Add the event listener
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup function
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
};
